import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  TextField,
} from '@mui/material'
import { useAppDispatch } from '../hooks'
import { setAuthenticated, setUserData } from '../stores/UserStore'
import { setGuestContext, setMeetingToken, setMeetingInfo } from '../stores/GuestStore'
import { setLoggedIn } from '../stores/UserStore'
import store from '../stores'
import type { ValidateTokenResponse, GuestContext } from '../../../types/Guest'
import GuestAvatarPicker, { GUEST_AVATAR_OPTIONS } from './GuestAvatarPicker'
import { getServerBaseUrl } from '../utils/serverUrl'
import AuthLightSurface from './auth/AuthLightSurface'

interface GuestJoinPageProps {
  token: string | null
  onComplete: () => void
}

type Phase = 'loading' | 'form' | 'joining' | 'error'

export default function GuestJoinPage({ token, onComplete }: GuestJoinPageProps) {
  const dispatch = useAppDispatch()
  const [phase, setPhase] = useState<Phase>('loading')
  const [error, setError] = useState<string | null>(null)
  const [meetingInfo, setLocalMeetingInfo] = useState<ValidateTokenResponse | null>(null)

  // Form state
  const [guestName, setGuestName] = useState('')
  const [guestCompany, setGuestCompany] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [guestAvatar, setGuestAvatar] = useState<string>('adam')

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No meeting token provided. Please use the link from your invitation email.')
      setPhase('error')
      return
    }

    let cancelled = false

    const validate = async () => {
      try {
        // Absolute: the client host (app-*.sevv.com) doesn't serve /api, the server host does.
        const resp = await fetch(`${getServerBaseUrl()}/api/guest/validate-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const data = await resp.json()

        if (cancelled) return

        if (!data.valid) {
          const messages: Record<string, string> = {
            expired: 'This meeting link has expired. The meeting may have already ended.',
            invalid: 'This meeting link is invalid. Please check the link from your invitation email.',
            cancelled: 'This meeting has been cancelled by the organiser.',
            removed: 'You are no longer on the invite list for this meeting.',
            // SC-409: the host company is suspended or deleted. Kept neutral on purpose: an
            // external guest must not learn the company's account state.
            unavailable: 'This meeting is currently unavailable.',
          }
          setError(messages[data.error] || 'Invalid meeting link.')
          setPhase('error')
          return
        }

        setLocalMeetingInfo(data)

        // Pre-fill from existing guest profile
        if (data.guestProfile) {
          setGuestName(data.guestProfile.displayName || '')
          setGuestCompany(data.guestProfile.company || '')
          // Only restore a saved avatar the picker can show, so preview and submitted value match
          if (GUEST_AVATAR_OPTIONS.some((a) => a.name === data.guestProfile.avatarPreset)) {
            setGuestAvatar(data.guestProfile.avatarPreset)
          }
        }

        setPhase('form')
      } catch (err) {
        if (!cancelled) {
          setError('Unable to validate your meeting link. Please try again.')
          setPhase('error')
        }
      }
    }

    validate()
    return () => { cancelled = true }
  }, [token])

  const handleJoin = useCallback(async () => {
    if (!guestName.trim() || !guestCompany.trim() || !guestEmail.trim() || !meetingInfo || !token) return

    // Verify the email matches the one from the invite token
    if (guestEmail.trim().toLowerCase() !== (meetingInfo.guestEmail || '').toLowerCase()) {
      setEmailError('This email does not match the invitation. Please enter the email address that received the invite.')
      return
    }
    setEmailError(null)

    setPhase('joining')

    const name = guestName.trim()
    const company = guestCompany.trim()

    // Dispatch minimal userData to Redux so InitializationPipeline doesn't reject
    dispatch(setUserData({
      id: `guest_${Date.now()}`,
      email: meetingInfo.guestEmail || '',
      username: name,
      role: 'user',
      createdAt: new Date().toISOString(),
      firstName: name.split(' ')[0],
      lastName: name.split(' ').slice(1).join(' '),
      avatar: guestAvatar,
      gameCharacter: guestAvatar,
      companyId: meetingInfo.companyId,
    }))
    dispatch(setAuthenticated(true))
    dispatch(setLoggedIn(true))

    // Set guest context in store
    const ctx: GuestContext = {
      bookingId: '', // will be resolved server-side from token
      email: meetingInfo.guestEmail || '',
      meetingRoomId: (meetingInfo as any).virtualRoomId || '',
      receptionRoomId: (meetingInfo as any).receptionRoomId || '',
      allowedZones: [
        String((meetingInfo as any).receptionRoomId || ''),
        String((meetingInfo as any).virtualRoomId || ''),
      ].filter(Boolean),
      isBeingEscorted: false,
      guestName: name,
      guestCompany: company,
      guestAvatar,
    }
    dispatch(setGuestContext(ctx))
    dispatch(setMeetingToken(token))
    dispatch(setMeetingInfo(meetingInfo))

    // Transition to the normal app flow — hide the guest page so the game canvas loads.
    // The app will auto-trigger room join via the guest auto-join effect.
    window.history.replaceState({}, document.title, '/')
    onComplete()
  }, [guestName, guestCompany, guestEmail, guestAvatar, meetingInfo, token, dispatch, onComplete])

  const formatDate = (iso: string | undefined) => {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return iso
    }
  }

  return (
    // Light like the sign-in screens, whatever mode the workspace runs in (SC-438).
    <AuthLightSurface>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          width: '100%',
          bgcolor: 'var(--sevv-bg-default, #fafafa)',
          p: 3,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
        }}
      >
        {/* Loading */}
        {phase === 'loading' && (
          <>
            <CircularProgress sx={{ color: 'var(--sevv-primary, #f97415)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
              Validating your meeting link…
            </Typography>
          </>
        )}

        {/* Joining */}
        {phase === 'joining' && (
          <>
            <CircularProgress sx={{ color: 'var(--sevv-primary, #f97415)', mb: 2 }} />
            <Typography variant="body1" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
              Joining the meeting…
            </Typography>
          </>
        )}

        {/* Form */}
        {phase === 'form' && meetingInfo && (
          <Box
            sx={{
              maxWidth: 460,
              width: '100%',
              bgcolor: 'var(--sevv-bg-paper, #ffffff)',
              color: 'var(--sevv-text-primary, #424242)',
              borderRadius: 2,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              p: 3,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: 24, color: 'var(--sevv-primary, #f97415)', mb: 2, textAlign: 'center' }}
            >
              {meetingInfo.companyName || 'Meeting'}
            </Typography>

            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mb: 0.5, color: 'var(--sevv-text-primary, #424242)' }}
            >
              {meetingInfo.meetingTitle || 'Meeting'}
            </Typography>

            <Box sx={{ mb: 2, color: 'var(--sevv-text-secondary, #757575)' }}>
              <Typography variant="body2">
                Host: {meetingInfo.hostName}
              </Typography>
              <Typography variant="body2">
                Room: {meetingInfo.roomName}
              </Typography>
              <Typography variant="body2">
                {formatDate(meetingInfo.startTime)}
                {meetingInfo.endTime && ` — ${new Date(meetingInfo.endTime).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`}
              </Typography>
            </Box>

            <TextField
              label="Your Name"
              required
              fullWidth
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: 'var(--sevv-text-secondary, #757575)' },
                '& .MuiOutlinedInput-root': {
                  color: 'var(--sevv-text-primary, #424242)',
                  '& .MuiInputBase-input': { color: 'var(--sevv-text-primary, #424242)' },
                },
              }}
            />

            <TextField
              label="Company"
              required
              fullWidth
              disabled
              value={guestCompany}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: 'var(--sevv-text-secondary, #757575)' },
                '& .MuiOutlinedInput-root': {
                  color: 'var(--sevv-text-primary, #424242)',
                  '& .MuiInputBase-input': { color: 'var(--sevv-text-primary, #424242)' },
                },
              }}
            />

            <TextField
              label="Email Address"
              required
              fullWidth
              type="email"
              value={guestEmail}
              onChange={(e) => {
                setGuestEmail(e.target.value)
                if (emailError) setEmailError(null)
              }}
              error={!!emailError}
              helperText={emailError || 'Enter the email address that received the invitation'}
              sx={{
                mb: 2,
                '& .MuiInputLabel-root': { color: 'var(--sevv-text-secondary, #757575)' },
                '& .MuiOutlinedInput-root': {
                  color: 'var(--sevv-text-primary, #424242)',
                  '& .MuiInputBase-input': { color: 'var(--sevv-text-primary, #424242)' },
                },
              }}
            />

            <GuestAvatarPicker value={guestAvatar} onChange={setGuestAvatar} />

            <Button
              variant="contained"
              fullWidth
              disabled={!guestName.trim() || !guestCompany.trim() || !guestEmail.trim()}
              onClick={handleJoin}
              sx={{
                bgcolor: 'var(--sevv-primary, #f97415)',
                '&:hover': { bgcolor: 'var(--sevv-primary-dark, #ea580c)' },
                textTransform: 'none',
                fontWeight: 600,
                py: 1.2,
              }}
            >
              Join Meeting
            </Button>
          </Box>
        )}

        {/* Error */}
        {phase === 'error' && error && (
          <Box sx={{ maxWidth: 440, width: '100%' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              fullWidth
              sx={{ bgcolor: 'var(--sevv-primary, #f97415)' }}
            >
              Try Again
            </Button>
          </Box>
        )}
      </Box>
    </AuthLightSurface>
  )
}
